<script setup lang="ts">
import { zodResolver } from "@primevue/forms/resolvers/zod";
import { z } from "zod";
import { reactive, ref } from "vue";

import { goFetch, isSuccessState } from "@/utils/network-utils";
import { ApiFetchResponse, RestfulPayload } from "@tmi/types/api/fetch";
import { createDefaultLogger } from "@/types/console-logger";
import { useUrlSearchParams } from '@vueuse/core';

const initialValues = reactive( {
  email: ''
} );

const resolver = zodResolver(
    z.object( {
      email:
          z.string()
              .min( 1, { message: 'Email is required' } )
              .max( 255, { message: 'Email cannot be longer than 255 characters' } )
              .email( { message: 'Please enter a valid email' } ),
      myEmail:
        z.string().nullable()
    } )
);

const loading = ref< boolean >( false );
const successfullySentForm = ref< boolean | null >( null );
const errorMessage = ref< string >();
const urlSearchParams = useUrlSearchParams();

type FreeNewsletterApiResponse = {
  requiredContactVerificationsSent?: boolean,
  contactsWhichRequireVerification?: string[],
  HttpStatusCode?: number,
  Message?: string,
  Errors?: {
    email: string[]
  }
}
const sendNewsLetterForm = async ( e: any ) => {
  if ( !e.valid ) {
    return null;
  }

  if(e.values.myEmail) {
    successfullySentForm.value = true;
    return;
  }

  loading.value = true;

  const utmAdParamValue: string = Array.isArray(urlSearchParams.utm_ad) ? urlSearchParams.utm_ad[0] : urlSearchParams.utm_ad;

  goFetch< FreeNewsletterApiResponse, RestfulPayload >( {
    endpoint: `registrar/marketing/enroll`,
    payload: { email: e.values.email, utm_ad: utmAdParamValue }
  } ).then( ( response: ApiFetchResponse< FreeNewsletterApiResponse > | null ) => {
    if ( response?.data && !isSuccessState( response?.status ) ) {
      if ( response.data.Message ) {
        errorMessage.value = response.data.Message;
      }

      successfullySentForm.value = false;
    } else {
      successfullySentForm.value = true;
    }
  } ).catch( ( err: any ) => {
    if ( typeof err === 'string' ) {
      createDefaultLogger().error( err );
    } else {
      createDefaultLogger().error( JSON.stringify( err.message ) );
    }
  } ).finally( () => {
    loading.value = false;
  } );
}
</script>

<template>
  <div v-if="null != successfullySentForm" class="font-bold text-center pt-5">
    <div v-if="!successfullySentForm">
      <span v-if="errorMessage">{{ errorMessage }}</span>
      <span v-else>Sorry, there was a problem with your request.</span>
    </div>
    <div v-else class="text-2xl"><i class="pi pi-check-circle"></i> Subscribed!</div>
  </div>

  <Form v-else v-slot="$form" :initial-values="initialValues" :resolver="resolver" @submit="sendNewsLetterForm" class="pt-5">
    <FormField v-slot="$field" name="email" initialValue="">
      <FloatLabel variant="on">
        <InputText
          id="emailNewsLetterInput"
          name="email"
          required
          inputId="email"
          :inputProps="{ name: 'email' }"
          autocomplete="email"
          type="email"
          :validateOnInput="true"
          :invalid="$field?.invalid"
          class="input border-solid w-full"/>
        <label for="emailNewsLetterInput">Enter your email address</label>
      </FloatLabel>
      <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
        {{ $field?.error.message }}
      </Message>

    </FormField>

      <FormField v-slot="$field" name="myEmail" initialValue="" style="position: absolute; left: -9999px">
        <InputText
            name="myEmail"
            variant="filled"
            inputId="myEmail"
            type="text"
            autocomplete="off"
            tabindex="-1"
            />
      </FormField>

    <button class="btn w-full" :class="{ 'dark:bg-neutral-900': !$form.valid }" v-if="loading" type="submit">
      <span class="w-6 h-6 rounded-full animate-spin border-2 border-solid border-base-100 border-t-transparent" />
    </button>
    <button v-else type="submit" :disabled="!$form.valid" class="w-full btn btn-primary text-white! my-2">
      <span>Get Free Newsletter</span>
    </button>
  </Form>
</template>
